import React, { useCallback, useContext, useState, useEffect } from 'react';

import { Button, Box, styled } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { WhiteboardContext } from '../../context/WhiteboardContext';
import { WhiteboardSessionContext } from '../../context/WhiteboardSessionContext';
import { SessionTypes } from '../../types';

export const toolTypes = [
    {
        icon: (
            // fa-hand
            // Font Awesome Pro 6.3.0 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license (Commercial License) Copyright 2023 Fonticons, Inc.
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
                <path d="M288 32c0-17.7-14.3-32-32-32s-32 14.3-32 32V240c0 8.8-7.2 16-16 16s-16-7.2-16-16V64c0-17.7-14.3-32-32-32s-32 14.3-32 32V336c0 1.5 0 3.1 .1 4.6L67.6 283c-16-15.2-41.3-14.6-56.6 1.4s-14.6 41.3 1.4 56.6L124.8 448c43.1 41.1 100.4 64 160 64H304c97.2 0 176-78.8 176-176V128c0-17.7-14.3-32-32-32s-32 14.3-32 32V240c0 8.8-7.2 16-16 16s-16-7.2-16-16V64c0-17.7-14.3-32-32-32s-32 14.3-32 32V240c0 8.8-7.2 16-16 16s-16-7.2-16-16V32z" />
            </svg>
        ),
        value: 'hand',
        canDisable: false
    },
    {
        icon: (
            // fa-mouse-pointer
            <svg viewBox="0 0 320 512" className="">
                <path d="M302.189 329.126H196.105l55.831 135.993c3.889 9.428-.555 19.999-9.444 23.999l-49.165 21.427c-9.165 4-19.443-.571-23.332-9.714l-53.053-129.136-86.664 89.138C18.729 472.71 0 463.554 0 447.977V18.299C0 1.899 19.921-6.096 30.277 5.443l284.412 292.542c11.472 11.179 3.007 31.141-12.5 31.141z" />
            </svg>
        ),
        value: 'selection',
        canDisable: false
    },
    {
        group: 'shapes',
        tools: [
            {
                icon: (
                    // fa-square
                    <svg viewBox="0 0 448 512">
                        <path d="M400 32H48C21.5 32 0 53.5 0 80v352c0 26.5 21.5 48 48 48h352c26.5 0 48-21.5 48-48V80c0-26.5-21.5-48-48-48z" />
                    </svg>
                ),
                value: 'rectangle',
                canDisable: true
            },
            {
                icon: (
                    // custom
                    <svg viewBox="0 0 223.646 223.646">
                        <path d="M111.823 0L16.622 111.823 111.823 223.646 207.025 111.823z" />
                    </svg>
                ),
                value: 'diamond',
                canDisable: true
            },
            {
                icon: (
                    // fa-circle
                    <svg viewBox="0 0 512 512">
                        <path d="M256 8C119 8 8 119 8 256s111 248 248 248 248-111 248-248S393 8 256 8z" />
                    </svg>
                ),
                value: 'ellipse',
                canDisable: true
            }
        ]
    },
    {
        group: 'lines',
        tools: [
            {
                icon: (
                    // fa-long-arrow-alt-right
                    <svg viewBox="0 0 448 512" className="rtl-mirror">
                        <path d="M313.941 216H12c-6.627 0-12 5.373-12 12v56c0 6.627 5.373 12 12 12h301.941v46.059c0 21.382 25.851 32.09 40.971 16.971l86.059-86.059c9.373-9.373 9.373-24.569 0-33.941l-86.059-86.059c-15.119-15.119-40.971-4.411-40.971 16.971V216z" />
                    </svg>
                ),
                value: 'arrow',
                canDisable: true
            },
            {
                icon: (
                    // custom
                    <svg width="16" height="4" viewBox="0 0 16 4" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect width="16" height="4" rx="0.2" />
                    </svg>
                ),
                value: 'line',
                canDisable: true
            }
        ]
    },
    {
        icon: (
            // fa-pencil
            <svg xmlns="http://www.w3.org/2000/svg" width="19" height="19" viewBox="0 0 19 19" fill="none">
                <path d="M0 15.25V19H3.75L14.81 7.94L11.06 4.19L0 15.25ZM17.71 5.04C18.1 4.65 18.1 4.02 17.71 3.63L15.37 1.29C14.98 0.899998 14.35 0.899998 13.96 1.29L12.13 3.12L15.88 6.87L17.71 5.04Z" />
            </svg>
        ),
        value: 'freedraw',
        canDisable: true
    },
    {
        icon: (
            // fa-font
            <svg viewBox="0 0 448 512">
                <path d="M432 416h-23.41L277.88 53.69A32 32 0 0 0 247.58 32h-47.16a32 32 0 0 0-30.3 21.69L39.41 416H16a16 16 0 0 0-16 16v32a16 16 0 0 0 16 16h128a16 16 0 0 0 16-16v-32a16 16 0 0 0-16-16h-19.58l23.3-64h152.56l23.3 64H304a16 16 0 0 0-16 16v32a16 16 0 0 0 16 16h128a16 16 0 0 0 16-16v-32a16 16 0 0 0-16-16zM176.85 272L224 142.51 271.15 272z" />
            </svg>
        ),
        value: 'text',
        canDisable: true
    },
    {
        icon: (
            // fa-font
            <svg
                width="18"
                height="18"
                viewBox="0 0 18 18"
                fill="none"
                stroke="none"
                xmlns="http://www.w3.org/2000/svg"
            >
                <rect x="10" y="14.5" width="8" height="2" rx="0.5" />
                <rect x="10" y="11.5" width="8" height="2" rx="0.5" />
                <rect x="0.464478" y="16.1213" width="8" height="2" rx="0.5" transform="rotate(-45 0.464478 16.1213)" />
                <rect
                    width="8"
                    height="2"
                    rx="0.5"
                    transform="matrix(-0.707107 -0.707107 -0.707107 0.707107 7.53552 16.1213)"
                />
                <rect y="3" width="8" height="2" rx="0.5" />
                <rect x="3" y="8" width="8" height="2" rx="0.5" transform="rotate(-90 3 8)" />
                <rect x="10" y="3" width="8" height="2" rx="0.5" />
            </svg>
        ),
        value: 'library',
        canDisable: true
    },
    {
        icon: (
            // fa-font
            <svg
                width="18"
                height="18"
                viewBox="0 0 512 512"
                fill="none"
                stroke="none"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path d="M56 0h80c13.3 0 24 10.7 24 24s-10.7 24-24 24H56c-4.4 0-8 3.6-8 8v80c0 13.3-10.7 24-24 24s-24-10.7-24-24V56C0 25.1 25.1 0 56 0zM376 0h80c30.9 0 56 25.1 56 56v80c0 13.3-10.7 24-24 24s-24-10.7-24-24V56c0-4.4-3.6-8-8-8H376c-13.3 0-24-10.7-24-24s10.7-24 24-24zM48 376v80c0 4.4 3.6 8 8 8h80c13.3 0 24 10.7 24 24s-10.7 24-24 24H56c-30.9 0-56-25.1-56-56V376c0-13.3 10.7-24 24-24s24 10.7 24 24zm464 0v80c0 30.9-25.1 56-56 56H376c-13.3 0-24-10.7-24-24s10.7-24 24-24h80c4.4 0 8-3.6 8-8V376c0-13.3 10.7-24 24-24s24 10.7 24 24zM180 128l6.2-16.4c3.5-9.4 12.5-15.6 22.5-15.6h94.7c10 0 19 6.2 22.5 15.6L332 128h36c26.5 0 48 21.5 48 48V336c0 26.5-21.5 48-48 48H144c-26.5 0-48-21.5-48-48V176c0-26.5 21.5-48 48-48h36zM320 256a64 64 0 1 0 -128 0 64 64 0 1 0 128 0z" />
            </svg>
        ),
        value: 'screenshot',
        canDisable: true
    }
];

const Container = styled(Box)`
    display: flex;
    flex-flow: row;
    flex-grow: 1;
    justify-content: center;
`;

const ButtonGroup = styled(Box)`
    display: flex;
    flex-direction: column;
    position: absolute;
    top: 0px;
    left: 0px;
    background-color: #fff;
`;

const EmptyButton = styled(Box)`
    width: 38.38px;
    height: 38.38px;
    position: relative;
    margin-left: 1px;
    margin-right: 1px;
`;

type ElementToolsProps = {
    canEdit: boolean;
};

const ElementTools = React.memo((props: ElementToolsProps) => {
    const { t } = useTranslation('whiteboard');
    const { canEdit } = props;
    const { flags } = useContext(WhiteboardContext);
    const {
        excalidrawAPIRef,
        activeTool,
        toolProperties,
        whiteboardSessionState: { session }
    } = useContext(WhiteboardSessionContext);
    const [openGroup, setOpenGroup] = useState<string | null>(null);
    const [lastOpenToolsInGroups, setLastOpenToolsInGroups] = useState<{
        [key: string]: string;
    }>({});

    /**
     * Called when user selects a tool.
     */
    const handleToolTypeChange = useCallback(
        (newToolType: string) => {
            if (!excalidrawAPIRef.current || !session) {
                return;
            }

            excalidrawAPIRef.current.setActiveTool({ type: newToolType });
            try {
                window.parent.postMessage(
                    {
                        func: 'whiteboard_tool_changed',
                        data: {
                            activeTool: newToolType
                        }
                    },
                    '*'
                );
            } catch (err) {}
        },
        [excalidrawAPIRef, session]
    );

    let tools = toolTypes.slice();

    if (!flags.screenshot || session?.type === SessionTypes.Presentation) {
        tools = toolTypes.filter((i) => i.value !== 'screenshot');
    }

    useEffect(() => {
        if (!canEdit) {
            handleToolTypeChange('hand');
        }
    }, [canEdit, handleToolTypeChange]);

    if (!canEdit) {
        tools = [];
    }
    if (!activeTool) {
        return <></>;
    }

    return (
        <Container>
            {tools.map((toolType) => {
                if (toolType.tools) {
                    const subTool = toolType.tools.find((i) => i.value === activeTool.type);
                    let lastOpenToolForThisGroup = lastOpenToolsInGroups[toolType.group];
                    let toolToDisplay = toolType.tools[0];
                    if (subTool) {
                        toolToDisplay = subTool;
                    } else if (lastOpenToolForThisGroup) {
                        toolToDisplay =
                            toolType.tools.find((i) => i.value === lastOpenToolForThisGroup) || toolToDisplay;
                    }
                    if (openGroup === toolType.group) {
                        return (
                            <EmptyButton>
                                <ButtonGroup
                                    onMouseLeave={() => {
                                        if (openGroup === toolType.group) {
                                            setOpenGroup(null);
                                        }
                                    }}
                                >
                                    {toolType.tools.map(
                                        (subTool) =>
                                            subTool.value === toolToDisplay.value && (
                                                <Button
                                                    key={subTool.value}
                                                    className="tool-button"
                                                    onClick={() => {
                                                        setLastOpenToolsInGroups((prev) => ({
                                                            ...prev,
                                                            [toolType.group]: subTool.value
                                                        }));
                                                        handleToolTypeChange(subTool.value);
                                                    }}
                                                    color={subTool.value === activeTool.type ? 'primary' : 'inherit'}
                                                    sx={{
                                                        minWidth: 0,
                                                        backgroundColor:
                                                            subTool.value !== activeTool.type
                                                                ? 'transparent'
                                                                : 'primary',
                                                        padding: '0.7rem',
                                                        margin: '1.5px',
                                                        ...toolProperties[subTool.value]
                                                    }}
                                                    variant="contained"
                                                    disableElevation
                                                    aria-label={t(`toolbarLabels.${subTool.value}`)}
                                                >
                                                    {subTool.icon}
                                                </Button>
                                            )
                                    )}
                                    {toolType.tools.map(
                                        (subTool) =>
                                            subTool.value !== toolToDisplay.value && (
                                                <Button
                                                    key={subTool.value}
                                                    className="tool-button"
                                                    onClick={() => {
                                                        setLastOpenToolsInGroups((prev) => ({
                                                            ...prev,
                                                            [toolType.group]: subTool.value
                                                        }));
                                                        handleToolTypeChange(subTool.value);
                                                    }}
                                                    color={subTool.value === activeTool.type ? 'primary' : 'inherit'}
                                                    sx={{
                                                        minWidth: 0,
                                                        backgroundColor:
                                                            subTool.value !== activeTool.type
                                                                ? 'transparent'
                                                                : 'primary',
                                                        padding: '0.7rem',
                                                        margin: '1.5px',
                                                        ...toolProperties[subTool.value]
                                                    }}
                                                    variant="contained"
                                                    disableElevation
                                                    aria-label={t(`toolbarLabels.${subTool.value}`)}
                                                >
                                                    {subTool.icon}
                                                </Button>
                                            )
                                    )}
                                </ButtonGroup>
                            </EmptyButton>
                        );
                    } else {
                        return (
                            <Button
                                key={toolToDisplay.value}
                                className="tool-button"
                                onMouseEnter={() => {
                                    setOpenGroup(toolType.group);
                                }}
                                onMouseLeave={() => {
                                    if (openGroup === toolType.group) {
                                        setOpenGroup(null);
                                    }
                                }}
                                onClick={() => {
                                    handleToolTypeChange(toolToDisplay.value);
                                }}
                                color={toolToDisplay.value === activeTool.type ? 'primary' : 'inherit'}
                                sx={{
                                    minWidth: 0,
                                    backgroundColor:
                                        toolToDisplay.value !== activeTool.type ? 'transparent' : 'primary',
                                    padding: '0.7rem',
                                    margin: '1.5px',
                                    ...toolProperties[toolToDisplay.value]
                                }}
                                variant="contained"
                                disableElevation
                                aria-label={t(`toolbarLabels.${toolToDisplay.value}`)}
                            >
                                {toolToDisplay.icon}
                            </Button>
                        );
                    }
                } else {
                    return (
                        <Button
                            key={toolType.value}
                            className="tool-button"
                            onClick={() => {
                                handleToolTypeChange(toolType.value);
                            }}
                            color={toolType.value === activeTool.type ? 'primary' : 'inherit'}
                            sx={{
                                minWidth: 0,
                                backgroundColor: toolType.value !== activeTool.type ? 'transparent' : 'primary',
                                padding: '0.7rem',
                                margin: '1.5px',
                                ...toolProperties[toolType.value]
                            }}
                            variant="contained"
                            disableElevation
                            aria-label={t(`toolbarLabels.${toolType.value}`)}
                        >
                            {toolType.icon}
                        </Button>
                    );
                }
            })}
        </Container>
    );
});

export default ElementTools;
